import { type RefObject, useLayoutEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getVisibleFocusables(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((el) => {
    if (el.hasAttribute('disabled')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(el);
    if (style.visibility === 'hidden' || style.display === 'none') return false;
    return true;
  });
}

/**
 * Trap Tab / Shift+Tab inside `rootRef` while `active`, move focus on open,
 * restore focus to the previously focused element on close (unless `restoreFocusOnDeactivate` is false).
 */
export function useFocusTrap(
  active: boolean,
  rootRef: RefObject<HTMLElement | null>,
  options?: {
    restoreFocusOnDeactivate?: boolean;
    /** Prefer focusing this element when open (must be inside `rootRef`). Falls back to first focusable. */
    initialFocusRef?: RefObject<HTMLElement | null>;
  },
): void {
  const restoreFocusOnDeactivate = options?.restoreFocusOnDeactivate !== false;
  const initialFocusRef = options?.initialFocusRef;
  const previousFocusRef = useRef<Element | null>(null);

  useLayoutEffect(() => {
    if (!active || !rootRef.current) return;

    previousFocusRef.current = document.activeElement;

    const root = rootRef.current;

    const focusInitial = (): void => {
      const preferred = initialFocusRef?.current;
      if (preferred && root.contains(preferred) && getVisibleFocusables(root).includes(preferred)) {
        preferred.focus();
        return;
      }
      const list = getVisibleFocusables(root);
      if (list.length > 0) list[0]?.focus();
    };

    let frame = 0;
    frame = requestAnimationFrame(() => {
      focusInitial();
    });

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;
      const list = getVisibleFocusables(root);
      if (list.length === 0) return;

      const first = list[0];
      const last = list[list.length - 1];
      if (!first || !last) return;

      const current = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        if (current === first || (current && !root.contains(current))) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (current === last || (current && !root.contains(current))) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handleKeyDown, true);
      if (restoreFocusOnDeactivate) {
        const prev = previousFocusRef.current as HTMLElement | null;
        if (prev && document.body.contains(prev) && typeof prev.focus === 'function') {
          try {
            prev.focus();
          } catch {
            /* ignore */
          }
        }
      }
    };
  }, [active, rootRef, restoreFocusOnDeactivate, initialFocusRef]);
}
